import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

type SeoConfig = {
  title: string;
  description: string;
  keywords: string;
};

const siteTitle = "Kolomaka Cleaning Services";
const siteUrl = "https://kolomakacleaning.co.za";

const seoByPath: Record<string, SeoConfig> = {
  "/": {
    title: "Kolomaka Cleaning Services | Cleaning and Pest Control",
    description:
      "Kolomaka Cleaning Services provides reliable residential cleaning, commercial cleaning, deep cleaning, pest control, hygiene, and sanitation services in Limpopo Province.",
    keywords:
      "Kolomaka Cleaning Services, cleaning services Limpopo, pest control, residential cleaning, commercial cleaning, deep cleaning, hygiene, sanitation",
  },
  "/about": {
    title: "About Kolomaka Cleaning Services | Limpopo Cleaning Company",
    description:
      "Learn about Kolomaka Cleaning Services, founded in 2020 in Limpopo Province, South Africa, and our mission to deliver dependable cleaning and pest control.",
    keywords:
      "about Kolomaka Cleaning Services, Limpopo cleaning company, pest control company South Africa, cleaning company profile",
  },
  "/services": {
    title: "Our Services | Kolomaka Cleaning Services",
    description:
      "Explore Kolomaka Cleaning Services for residential cleaning, commercial cleaning, office cleaning, deep cleaning, post construction cleaning, and pest control.",
    keywords:
      "cleaning services, pest control services, residential cleaning, office cleaning, commercial cleaning, deep cleaning, post construction",
  },
  "/contact": {
    title: "Contact Kolomaka Cleaning Services | Get a Quote",
    description:
      "Contact Kolomaka Cleaning Services for a quote on cleaning, hygiene, sanitation, and pest control services in Limpopo Province.",
    keywords:
      "contact Kolomaka Cleaning Services, cleaning quote, pest control quote, Limpopo contact cleaning company",
  },
};

const applyMetaTag = (name: string, content: string, property = false) => {
  const selector = property
    ? `meta[property='${name}']`
    : `meta[name='${name}']`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    if (property) {
      element.setAttribute("property", name);
    } else {
      element.setAttribute("name", name);
    }
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

export const SeoManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname.replace(/\/$/, "") || "/";
    const seo = seoByPath[pathname] ?? seoByPath["/"];
    const currentUrl = `${siteUrl}${pathname === "/" ? "" : pathname}`;

    document.title = seo.title;
    applyMetaTag("description", seo.description);
    applyMetaTag("keywords", seo.keywords);
    applyMetaTag("theme-color", "#00437A");
    applyMetaTag("robots", "index, follow");

    applyMetaTag("og:title", seo.title, true);
    applyMetaTag("og:description", seo.description, true);
    applyMetaTag("og:type", "website", true);
    applyMetaTag("og:url", currentUrl, true);
    applyMetaTag("og:site_name", siteTitle, true);

    applyMetaTag("twitter:card", "summary_large_image");
    applyMetaTag("twitter:title", seo.title);
    applyMetaTag("twitter:description", seo.description);

    let canonical = document.head.querySelector<HTMLLinkElement>(
      "link[rel='canonical']",
    );

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", currentUrl);
  }, [location.pathname]);

  return null;
};
